import React, { useContext, useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Snackbar,
  CircularProgress,
  Box,
  InputAdornment,
  Alert,
  Divider
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { CurrencyContext } from '../contexts/CurrencyContext';
import { OrgContext } from '../contexts/OrgContext';
import { AuthContext } from '../contexts/AuthContext';
import { updateOrganizationMeta, getOrganizationById } from '../services/organizationService';

const availableCurrencies = [
  { code: 'BDT', symbol: '৳', label: 'Bangladeshi Taka' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', label: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', label: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee' },
];

const Settings = () => {
  const { currency, changeCurrency } = useContext(CurrencyContext);
  const { orgId, setOrgLogoURL } = useContext(OrgContext);
  const { token } = useContext(AuthContext);

  const [selectedFile, setSelectedFile] = useState(null);
  const [success, setSuccess] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState('success');
  const [taxNumber, setTaxNumber] = useState('');
  const [orgAddress, setOrgAddress] = useState('');
  const [financingCost, setFinancingCost] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isLocalUser, setIsLocalUser] = useState(false);
  const [googleLinkLoading, setGoogleLinkLoading] = useState(false);

  useEffect(() => {
    if (orgId) {
      setLoading(true);
      getOrganizationById(orgId)
        .then(org => {
          // If org has meta data with tax number and address, populate the fields
          if (org.meta) {
            if (org.meta.taxNumber) setTaxNumber(org.meta.taxNumber);
            if (org.meta.orgAddress) setOrgAddress(org.meta.orgAddress);
            if (org.meta.financingCost) setFinancingCost(org.meta.financingCost);
          }
        })
        .catch(err => {
          console.error("Error fetching organization details:", err);
          setSuccess('Failed to load organization details');
          setSnackbarType('error');
          setSuccessOpen(true);
        })
        .finally(() => setLoading(false));
    }
  }, [orgId]);

  // Check if user is a local user (password-based) on component mount
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsLocalUser(payload.type === 'local');
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [token]);

  const handleCurrencyChange = (event) => {
    changeCurrency(event.target.value);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleLogoUpload = () => {
    if (!selectedFile) {
      setSuccess('No file selected. Please select a file first.');
      setSnackbarType('error');
      setSuccessOpen(true);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataURL = reader.result;
      setOrgLogoURL(dataURL);
      // Dispatch a custom event so Header can update
      window.dispatchEvent(new Event('orgLogoUpdated'));
      setSuccess('Organization logo updated!');
      setSnackbarType('success');
      setSuccessOpen(true);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleSaveSettings = async () => {
    if (!orgId) {
      setSuccess('No organization selected.');
      setSnackbarType('error');
      setSuccessOpen(true);
      return;
    }

    setSaving(true);
    try {
      // Create meta object with tax number, address, and financing cost
      const metaUpdates = {
        taxNumber,
        orgAddress,
        financingCost: financingCost ? parseFloat(financingCost) : null
      };

      // Call API to update organization meta
      await updateOrganizationMeta(orgId, metaUpdates);
      setSuccess('Settings saved successfully!');
      setSnackbarType('success');
      setSuccessOpen(true);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSuccess(error.response?.data?.message || 'Failed to save settings.');
      setSnackbarType('error');
      setSuccessOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleLinkGoogleAccount = () => {
    setGoogleLinkLoading(true);
    // Redirect to Google OAuth with a flag indicating this is for account linking
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    window.location.href = `${backendUrl}/auth/google?link=true`;
  };

  if (loading) {
    return (
      <Paper className="surface-card" sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className="surface-card" sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Settings
      </Typography>
      <Grid container spacing={3}>
        {/* Currency Selection */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="currency-select-label">
              Currency
            </InputLabel>
            <Select
              labelId="currency-select-label"
              id="currency-select"
              value={currency}
              label="Currency"
              onChange={handleCurrencyChange}
            >
              {availableCurrencies.map((curr) => (
                <MenuItem key={curr.code} value={curr.code}>
                  {curr.label} ({curr.code}) {curr.symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Tax Number Field */}
        <Grid item xs={12}>
          <TextField
            label="Tax Number"
            value={taxNumber}
            onChange={(e) => setTaxNumber(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="Enter organization tax number"
          />
        </Grid>

        {/* Organization Address Field */}
        <Grid item xs={12}>
          <TextField
            label="Organization Address"
            value={orgAddress}
            onChange={(e) => setOrgAddress(e.target.value)}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Enter organization address"
          />
        </Grid>
        
        {/* Financing Cost Field */}
        <Grid item xs={12}>
          <TextField
            label="Annual Financing Cost"
            value={financingCost}
            onChange={(e) => {
              // Only allow numbers and decimal point
              if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                setFinancingCost(e.target.value);
              }
            }}
            fullWidth
            variant="outlined"
            placeholder="Enter annual financing cost percentage"
            type="text"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            This is used for inventory valuation and financing cost calculations.
          </Typography>
        </Grid>

        {/* Organization Logo Upload */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Organization Logo
          </Typography>
          <TextField
            type="file"
            onChange={handleFileChange}
            inputProps={{ accept: 'image/*' }}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" color="primary" onClick={handleLogoUpload} disabled={!selectedFile}>
            Upload Logo
          </Button>
        </Grid>

        {/* Google Account Linking Section */}
        {isLocalUser && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Account Migration
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Migrate to Google Sign-In for enhanced security and seamless access. 
                  Password-based authentication will be disabled soon.
                </Typography>
              </Alert>
              <Button
                onClick={handleLinkGoogleAccount}
                variant="contained"
                startIcon={<GoogleIcon />}
                disabled={googleLinkLoading}
              >
                {googleLinkLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Link Google Account'
                )}
              </Button>
            </Grid>
          </>
        )}

        {/* Save Button */}
        <Grid item xs={12} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Settings'}
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={success}
        ContentProps={{
          sx: {
            color: '#ffffff',
            backgroundColor:
              snackbarType === 'success' ? 'primary.main' : 'error.main',
            textAlign: 'center'
          }
        }}
      />
    </Paper>
  );
};

export default Settings;
